import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import { validateBlockConnectionPosition } from '@sb/remote-control/util/blockConnectorValidation';

/**
 * Types matching the routine runner schema for "Else" statements.
 * Else executes its substeps if all previous If/ElseIf conditions were false.
 * Unlike ElseIf, it has no condition - it always runs if reached.
 */
export namespace ElseStep {
  export const name = 'Else';
  export const description =
    'Run substeps if all previous conditions were false';
  export const isDecorator = true;
  export const librarySection = Step.LibrarySection.Hidden;
  export const librarySort = '5';
  export const argumentKind = 'Else';

  /** Marks this step as the end of an if/else conditional block */
  export const blockConnector: Step.BlockConnector = {
    blockType: 'ifElse',
    role: 'end',
  };

  /** Arguments to be passed to the routine runner - Else has no condition */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Else',
      args: {},
    };
  };

  export const validator: Step.Validator = ({ step, routine }) => {
    // Validate block position - must be part of a valid if/else block
    validateBlockConnectionPosition(
      step,
      routine,
      blockConnector,
      'Else must be part of a valid If block',
    );
  };
}

ElseStep satisfies Step.StepKindInfo;
